/**
 * Memory Graph — deterministic layout (G2).
 *
 * `computeGraphLayout` is a PURE, DETERMINISTIC function of its input: identical
 * `LayoutInput` (including `seed` and `viewport`) always yields a bit-for-bit
 * identical `LayoutResult`. There is no `Math.random`, no `Date.now`, no
 * `performance.now` and no wall clock anywhere in this module — every random
 * draw comes from a hand-written seeded PRNG below. No new runtime dependency is
 * introduced; the whole force-like relaxation is written by hand.
 *
 * All geometry is normalized to the unit box [0,1] × [0,1] at the reference
 * aspect ({@link LAYOUT_REFERENCE}); G3 multiplies by the rendered stage size.
 * The layout never invents or widens data: it only places the nodes/edges/
 * clusters it is given and echoes the tier radii from the frozen contract.
 */

import type {
  LayoutEdge,
  LayoutInput,
  LayoutNode,
  LayoutResult,
  MinimapDot,
  NodeSizeTier,
  NormalizedPoint,
} from "./types";
import { LAYOUT_REFERENCE, NODE_RADIUS_BY_TIER } from "./types";

/* ------------------------------------------------------------------------- *
 * Seeded PRNG (hand-written, deterministic)
 *
 * mulberry32: a tiny, well-understood 32-bit generator. Given a uint32 state it
 * returns a stream of floats in [0, 1). It is fully deterministic — the same
 * seed always produces the same sequence — and uses only integer bit math, so
 * there is no reliance on any platform random source.
 * ------------------------------------------------------------------------- */

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    // Advance the 32-bit state by a fixed odd increment, then avalanche it.
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * FNV-1a 32-bit string hash. Turns a node/cluster id into a stable uint32 so a
 * node's initial placement depends on its identity (not its array index),
 * giving an organic but reproducible scatter.
 */
function hashString(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Deterministically fold the master seed together with an id-derived hash. */
function mixSeed(seed: number, salt: string): number {
  let h = (seed ^ hashString(salt)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** A per-id PRNG stream, seeded from (master seed, id). */
function prngFor(seed: number, salt: string): () => number {
  return mulberry32(mixSeed(seed, salt));
}

/* ------------------------------------------------------------------------- *
 * Tuning constants (all fixed — no schedule ever depends on wall clock)
 * ------------------------------------------------------------------------- */

/** Fixed relaxation length. Bounded so a dense 240-node graph stays fast. */
const ITERATIONS = 260;
/**
 * Ceiling on the overlap-removal passes run after relaxation. The pass stops as
 * soon as nothing is under its separation any more, so a small graph pays two or
 * three of these and only a crowded one goes near the ceiling; the ceiling
 * exists so the loop can never depend on convergence it might not reach.
 *
 * It used to be a flat 18 passes, taken whether they were needed or not and
 * stopping whether the job was done or not. On a 151-entity graph it was not
 * done: 56 pairs were still inside their own minimum separation and two node
 * marks were drawn overlapping. Raising the flat count was measured and does not
 * fix it — 18 -> 600 passes moved the violations only from 73 to 61 and cost
 * 96 -> 296 ms — because the cloud was jammed against the box (see
 * {@link REPULSION_RANGE}); once it is not, the pass settles well inside this
 * ceiling.
 */
const COLLISION_PASSES_MAX = 64;

/**
 * How far apart two nodes still repel, as a multiple of the ideal spacing `k`.
 *
 * Repulsion was summed over every pair at every iteration. Individually the
 * k²/d term is a rounding error at ten times k; summed over 150 partners it is
 * a large net outward push, and there is nothing to balance it — gravity pulls
 * each node toward its OWN cluster centre, and those centres are themselves
 * spread across the box, so the scene has no global centring force at all. The
 * cloud therefore expanded until the walls stopped it: after relaxation alone,
 * 95 of 151 nodes sat exactly on a margin. Two nodes stacked against the same
 * wall cannot be separated — the direction that would part them runs along the
 * wall into the next node, and the only escape is inward, which a pairwise push
 * never chooses. That, not the number of passes, is why the overlaps survived.
 *
 * Cutting the sum at a few k removes the spurious global push and leaves the
 * local spacing FR is actually about. Measured on the 151-node graph
 * (pinned-to-margin / pairs under separation / marks drawn overlapping):
 *   no cutoff  76 / 56 / 2      2k  44 / 42 / 0
 *   3k         57 / 35 / 0      4k  57 / 17 / 0      6k  75 / 24 / 1
 * Four is where the map both stops touching the walls and stops overlapping.
 */
const REPULSION_RANGE = 4;

/**
 * How much of each pass's accumulated correction is applied.
 *
 * The separation pass used to resolve pairs one at a time, in place: parting
 * (a, b) and then immediately parting (a, c) using a's new position. In a
 * crowded neighbourhood that thrashes — c pushes a straight back into b, and
 * the pair never settles however long it runs. Measured: 400 passes left 34
 * pairs overlapping and made no further progress.
 *
 * Every correction is now computed against the same snapshot and applied
 * together, which is what lets a node in a crowd move by the SUM of what its
 * neighbours need rather than by whichever one was considered last. Each pair
 * contributes half its shortfall to each node, so a factor of two applies that
 * shortfall in full — the classic over-relaxation, and the point at which this
 * converges. Measured on the 151-node graph, pairs of marks left overlapping:
 *   0.6 -> 53   1.0 -> 51   1.2 -> 22   1.5 -> 5   1.9 -> 2   2.0 -> 0
 */
const COLLISION_RELAXATION = 2;

/** Layout box margins (fraction of the unit box). */
const MARGIN_X = 0.05;
const MARGIN_TOP = 0.05;
/** Wider bottom margin so node labels (drawn below the circle) do not clip. */
const MARGIN_BOTTOM = 0.09;

/** Cooling schedule endpoints for the per-node displacement cap. */
const TEMP_START = 0.08;
const TEMP_END = 0.002;

/** Cluster gravity: hubs are pinned harder so cluster centers stay distinct. */
const GRAVITY_MEMBER = 0.08;
const GRAVITY_HUB = 0.18;

/** Ring the cluster centers sit on (semi-axes of the unit box). */
const CLUSTER_RING_X = 0.3;
const CLUSTER_RING_Y = 0.26;
/** Deterministic jitter applied to each cluster center for an organic feel. */
const CLUSTER_JITTER = 0.05;
/**
 * Above this many clusters the single ring stops being a ring.
 *
 * Every unconnected entity becomes its own single-member cluster (README §3,
 * step C), so a real tenant does not produce four cluster centers — it produces
 * one per hub plus one per orphan, dozens of them, and the ring placed all of
 * them on the same ellipse. At thirty centers the arc between neighbours is
 * shorter than a node's own diameter, which is why the map grew a band of
 * circles sitting on each other with their names interleaved. Past this count
 * the centers fill the *area* instead (see {@link spiralCenter}).
 */
const CLUSTER_RING_CAPACITY = 10;
/** Golden angle — the phyllotaxis constant that spaces a spiral evenly. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
/**
 * Semi-axes of the box the area-filling spiral spreads cluster centers over.
 *
 * Deliberately inside the clamp below (0.12…0.88) once the jitter is added:
 * `0.5 ± (spread + CLUSTER_JITTER / 2)`. A spread that reaches past it does not
 * simply crop — every center in the outer ring lands on the *same* clamped
 * corner point, and two single-member clusters pinned to one point are two
 * circles drawn exactly on top of each other, which no separation pass can undo
 * because neither has anywhere left to go.
 */
const CLUSTER_SPREAD_X = 0.35;
const CLUSTER_SPREAD_Y = 0.33;

/** Extra separation (beyond touching radii) reserved for a node's label. */
const LABEL_PAD = 0.006;

/** Prototype edge-bow: perpendicular offset = min(0.042, length·0.13). */
const EDGE_BOW_FRACTION = 0.13;
const EDGE_BOW_MAX = 0.042; // 60px / 1440 reference width ≈ 0.042.

/** Guards against divide-by-zero on coincident points. */
const EPSILON = 1e-9;

function clamp(value: number, lo: number, hi: number): number {
  if (value < lo) return lo;
  if (value > hi) return hi;
  return value;
}

/**
 * The stage's width-to-height ratio, which every distance below is measured in.
 *
 * All geometry here is normalized to the unit box, but the box is not square:
 * it is painted onto a stage whose width is ~1.7× its height, and a node's
 * radius is a fraction of the *width* in both directions (the circle keeps
 * `aspect-ratio: 1`). So two nodes a normalized 0.05 apart are 72 px apart side
 * by side and 42 px apart one above the other — and every separation rule that
 * compared `hypot(dx, dy)` against a sum of radii was, vertically, letting them
 * overlap by 42%. Converting `dy` into the same units as `dx` and the radii is
 * what makes "these two circles do not touch" mean the same thing in both
 * directions.
 */
function aspectOf(viewport: LayoutInput["viewport"]): number {
  const { width, height } = viewport;
  if (!Number.isFinite(width) || !Number.isFinite(height) || height <= 0) {
    return LAYOUT_REFERENCE.aspect;
  }
  return clamp(width / height, 0.2, 6);
}

/**
 * A cluster center from the area-filling spiral used past
 * {@link CLUSTER_RING_CAPACITY}. Sunflower placement: radius grows as √index so
 * successive centers cover the ellipse evenly rather than crowding its rim.
 */
function spiralCenter(index: number, count: number): NormalizedPoint {
  const radial = Math.sqrt((index + 0.5) / count);
  const angle = index * GOLDEN_ANGLE;
  return {
    x: 0.5 + Math.cos(angle) * radial * CLUSTER_SPREAD_X,
    y: 0.5 + Math.sin(angle) * radial * CLUSTER_SPREAD_Y,
  };
}

/* ------------------------------------------------------------------------- *
 * Internal mutable node state (never leaves this module)
 * ------------------------------------------------------------------------- */

interface NodeState {
  readonly id: string;
  readonly radius: number;
  readonly sizeTier: NodeSizeTier;
  readonly clusterId: string;
  readonly isHub: boolean;
  x: number;
  y: number;
  dx: number;
  dy: number;
}

/**
 * Deterministically place each cluster center on a jittered ring so clusters
 * disperse evenly. Largest clusters take the first ring slots. Returns a map
 * from clusterId to its center point; every clusterId seen on a node is covered.
 */
function placeClusterCenters(input: LayoutInput): Map<string, NormalizedPoint> {
  // Union of contract clusters and any clusterId referenced by a node, so no
  // node is ever left without a gravity target.
  const counts = new Map<string, number>();
  for (const cluster of input.clusters) {
    counts.set(cluster.id, cluster.memberNodeIds.length);
  }
  for (const node of input.nodes) {
    if (!counts.has(node.clusterId)) {
      counts.set(node.clusterId, 0);
    }
    counts.set(node.clusterId, (counts.get(node.clusterId) ?? 0) + 1);
  }

  const ordered = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
  });

  const centers = new Map<string, NormalizedPoint>();
  const count = ordered.length;
  // Past the ring's capacity the centers spread over the whole box instead of
  // around one ellipse; the jitter is the same seeded draw either way, so a
  // graph that stays under the threshold lays out exactly as it always did.
  const crowded = count > CLUSTER_RING_CAPACITY;
  ordered.forEach(([clusterId], index) => {
    if (count <= 1) {
      centers.set(clusterId, { x: 0.5, y: 0.5 });
      return;
    }
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / count;
    const base = crowded
      ? spiralCenter(index, count)
      : {
          x: 0.5 + Math.cos(angle) * CLUSTER_RING_X,
          y: 0.5 + Math.sin(angle) * CLUSTER_RING_Y,
        };
    const jitter = prngFor(input.seed, `cluster#${clusterId}`);
    const jx = (jitter() - 0.5) * CLUSTER_JITTER;
    const jy = (jitter() - 0.5) * CLUSTER_JITTER;
    centers.set(clusterId, {
      x: clamp(base.x + jx, 0.12, 0.88),
      y: clamp(base.y + jy, 0.12, 0.88),
    });
  });
  return centers;
}

/** Radius-aware clamp keeping a node's whole circle inside the layout box. */
function clampToBox(state: NodeState): void {
  const r = state.radius;
  state.x = clamp(state.x, MARGIN_X + r, 1 - MARGIN_X - r);
  state.y = clamp(state.y, MARGIN_TOP + r, 1 - MARGIN_BOTTOM - r);
}

/**
 * Seed the initial positions: hubs at their cluster center, members scattered
 * around it at a seeded angle and radius. The scatter radius grows with cluster
 * size so a crowded cluster starts less compressed.
 */
function seedPositions(
  input: LayoutInput,
  centers: Map<string, NormalizedPoint>,
): NodeState[] {
  const memberCount = new Map<string, number>();
  for (const node of input.nodes) {
    memberCount.set(node.clusterId, (memberCount.get(node.clusterId) ?? 0) + 1);
  }
  const hubIds = new Set<string>();
  for (const cluster of input.clusters) {
    hubIds.add(cluster.hubNodeId);
  }

  return input.nodes.map((node) => {
    const center = centers.get(node.clusterId) ?? { x: 0.5, y: 0.5 };
    const isHub = hubIds.has(node.id);
    const radius = NODE_RADIUS_BY_TIER[node.sizeTier];
    let x = center.x;
    let y = center.y;
    if (!isHub) {
      const rng = prngFor(input.seed, node.id);
      const size = memberCount.get(node.clusterId) ?? 1;
      const spread = clamp(0.06 + 0.01 * Math.sqrt(size), 0.06, 0.16);
      const angle = rng() * 2 * Math.PI;
      const dist = spread * (0.35 + 0.65 * rng());
      x = center.x + Math.cos(angle) * dist;
      y = center.y + Math.sin(angle) * dist;
    }
    const state: NodeState = {
      id: node.id,
      radius,
      sizeTier: node.sizeTier,
      clusterId: node.clusterId,
      isHub,
      x,
      y,
      dx: 0,
      dy: 0,
    };
    clampToBox(state);
    return state;
  });
}

/**
 * One Fruchterman–Reingold-style relaxation: repulsion between every pair,
 * spring attraction along edges, gravity toward cluster centers, then a cooled,
 * capped integration step. All magnitudes are finite and divide-by-zero safe.
 */
function relax(
  states: NodeState[],
  input: LayoutInput,
  centers: Map<string, NormalizedPoint>,
  aspect: number,
): void {
  const n = states.length;
  if (n === 0) return;
  const indexById = new Map<string, number>();
  states.forEach((s, i) => indexById.set(s.id, i));

  // Ideal node spacing shrinks as the graph grows (classic FR scaling).
  const k = clamp(0.75 * Math.sqrt(1 / n), 0.045, 0.16);
  const k2 = k * k;
  const repulsionCutoff = REPULSION_RANGE * k;

  for (let iter = 0; iter < ITERATIONS; iter += 1) {
    for (const s of states) {
      s.dx = 0;
      s.dy = 0;
    }

    // Repulsion between every unordered pair (O(n²); bounded n keeps it cheap).
    // Distances are measured in width units — `dy` divided by the stage aspect —
    // so a pair stacked vertically feels the same push as one side by side.
    for (let i = 0; i < n; i += 1) {
      const a = states[i];
      for (let j = i + 1; j < n; j += 1) {
        const b = states[j];
        // Reject on the axes before paying for a square root: the distance is
        // never smaller than either component, so a pair further apart than the
        // cutoff on one axis is out of range whatever the other axis says.
        if (Math.abs(a.x - b.x) > repulsionCutoff) continue;
        if (Math.abs(a.y - b.y) / aspect > repulsionCutoff) continue;
        const { unitX, unitY, dist } = separation(a, b, i, j, aspect);
        if (dist > repulsionCutoff) continue;
        // Coincident nodes repel hardest, not least: `dist` is genuinely zero
        // there, and the floor only keeps the force finite.
        const force = k2 / Math.max(dist, EPSILON);
        const ux = unitX * force;
        // Back into unit-box units: a push of one width unit downward has to
        // travel `aspect` times as far along the shorter axis.
        const uy = unitY * force * aspect;
        a.dx += ux;
        a.dy += uy;
        b.dx -= ux;
        b.dy -= uy;
      }
    }

    // Spring attraction along each edge (endpoints pulled together).
    for (const edge of input.edges) {
      const i = indexById.get(edge.sourceNodeId);
      const j = indexById.get(edge.targetNodeId);
      if (i === undefined || j === undefined || i === j) continue;
      const a = states[i];
      const b = states[j];
      const ddx = a.x - b.x;
      const ddy = a.y - b.y;
      const dist = Math.hypot(ddx, ddy) || EPSILON;
      const force = (dist * dist) / k;
      const ux = (ddx / dist) * force;
      const uy = (ddy / dist) * force;
      a.dx -= ux;
      a.dy -= uy;
      b.dx += ux;
      b.dy += uy;
    }

    // Gravity toward the cluster center (hubs pinned harder).
    for (const s of states) {
      const center = centers.get(s.clusterId) ?? { x: 0.5, y: 0.5 };
      const g = s.isHub ? GRAVITY_HUB : GRAVITY_MEMBER;
      s.dx += (center.x - s.x) * g;
      s.dy += (center.y - s.y) * g;
    }

    // Cooled, capped integration.
    const temp =
      TEMP_END + (TEMP_START - TEMP_END) * (1 - iter / (ITERATIONS - 1));
    for (const s of states) {
      const len = Math.hypot(s.dx, s.dy);
      if (len > EPSILON) {
        const scale = Math.min(len, temp) / len;
        s.x += s.dx * scale;
        s.y += s.dy * scale;
      }
      clampToBox(s);
    }
  }
}

/**
 * The gap between two nodes, in width units, with the unit direction to push
 * them apart along.
 *
 * The coincident case is the one that mattered. It used to substitute an
 * arbitrary direction vector and then take *that vector's own length* as the
 * distance — roughly 0.7, comfortably larger than any separation threshold — so
 * two nodes at exactly the same point failed the "are these too close" test and
 * were skipped by every pass. Four pairs of circles sat perfectly on top of each
 * other in the corners of the map because of it. The substituted vector is a
 * direction and nothing else; the distance in that case is zero, which is what
 * it has always been.
 */
function separation(
  a: NodeState,
  b: NodeState,
  i: number,
  j: number,
  aspect: number,
): { unitX: number; unitY: number; dist: number } {
  const rawX = a.x - b.x;
  const rawY = (a.y - b.y) / aspect;
  const dist = Math.hypot(rawX, rawY);
  if (dist >= EPSILON) {
    return { unitX: rawX / dist, unitY: rawY / dist, dist };
  }
  // Deterministic symmetry break for coincident nodes.
  const fx = ((i - j) % 5) - 2 + 0.5;
  const fy = ((i + j) % 5) - 2 + 0.5;
  const len = Math.hypot(fx, fy) || 1;
  return { unitX: fx / len, unitY: fy / len, dist: 0 };
}

/**
 * Push apart any pair closer than their combined radii (plus a label pad),
 * re-clamping each pass, until nothing is inside its separation any more.
 *
 * The guarantee is only a guarantee in the units the screen uses: a radius is a
 * fraction of the stage's *width*, and so is the horizontal gap — but a vertical
 * gap of the same number is `aspect` times smaller in pixels. Measuring the
 * separation with `dy` converted into width units is what stops two circles a
 * "safe" 0.05 apart from covering each other's top and bottom halves.
 */
function resolveCollisions(states: NodeState[], aspect: number): void {
  const n = states.length;
  for (let pass = 0; pass < COLLISION_PASSES_MAX; pass += 1) {
    for (const s of states) {
      s.dx = 0;
      s.dy = 0;
    }
    let violated = false;
    for (let i = 0; i < n; i += 1) {
      const a = states[i];
      for (let j = i + 1; j < n; j += 1) {
        const b = states[j];
        const minSep = (a.radius + b.radius) * 1.06 + LABEL_PAD;
        // Reject on the axes before paying for a square root: the distance is
        // never smaller than either component, so most of the 11 325 pairs on a
        // real graph are dismissed with two comparisons.
        if (Math.abs(a.x - b.x) >= minSep) continue;
        if (Math.abs(a.y - b.y) / aspect >= minSep) continue;
        const { unitX, unitY, dist } = separation(a, b, i, j, aspect);
        if (dist >= minSep) continue;
        violated = true;
        const half = (minSep - dist) / 2;
        a.dx += unitX * half;
        a.dy += unitY * half * aspect;
        b.dx -= unitX * half;
        b.dy -= unitY * half * aspect;
      }
    }
    if (!violated) return;
    for (const s of states) {
      s.x += s.dx * COLLISION_RELAXATION;
      s.y += s.dy * COLLISION_RELAXATION;
      clampToBox(s);
    }
  }
}

/** Quadratic-Bézier point at t (used for the strong-edge label anchor). */
function quadraticPoint(
  start: NormalizedPoint,
  control: NormalizedPoint,
  end: NormalizedPoint,
  t: number,
): NormalizedPoint {
  const mt = 1 - t;
  return {
    x: mt * mt * start.x + 2 * mt * t * control.x + t * t * end.x,
    y: mt * mt * start.y + 2 * mt * t * control.y + t * t * end.y,
  };
}

/** Build the drawable edges from the final node positions. */
function buildEdges(
  input: LayoutInput,
  positions: Map<string, NormalizedPoint>,
): LayoutEdge[] {
  const edges: LayoutEdge[] = [];
  for (const edge of input.edges) {
    const start = positions.get(edge.sourceNodeId);
    const end = positions.get(edge.targetNodeId);
    if (!start || !end) continue; // never fabricate an endpoint.

    const mx = (start.x + end.x) / 2;
    const my = (start.y + end.y) / 2;
    const ddx = end.x - start.x;
    const ddy = end.y - start.y;
    const len = Math.hypot(ddx, ddy) || 1;
    const off = Math.min(EDGE_BOW_MAX, len * EDGE_BOW_FRACTION);
    // Bow the control point along the edge normal (matches the prototype).
    const control: NormalizedPoint = {
      x: mx - (ddy / len) * off,
      y: my + (ddx / len) * off,
    };
    const labelAnchor =
      edge.strong && edge.relationType !== null
        ? quadraticPoint(start, control, end, 0.5)
        : null;

    edges.push({
      sourceNodeId: edge.sourceNodeId,
      targetNodeId: edge.targetNodeId,
      start,
      end,
      control,
      labelAnchor,
      weight: edge.weight,
      strong: edge.strong,
      relationType: edge.relationType,
      ...(edge.states ? { states: edge.states } : {}),
    });
  }
  return edges;
}

/**
 * Compute the deterministic Memory Graph layout for a graph view.
 *
 * Pure and side-effect free: the same {@link LayoutInput} always returns an
 * equal {@link LayoutResult}. Empty input yields an empty (but well-formed)
 * result and never throws.
 */
export function computeGraphLayout(input: LayoutInput): LayoutResult {
  if (input.nodes.length === 0) {
    return { nodes: [], edges: [], minimap: [], seed: input.seed };
  }

  const aspect = aspectOf(input.viewport);
  const centers = placeClusterCenters(input);
  const states = seedPositions(input, centers);
  relax(states, input, centers, aspect);
  resolveCollisions(states, aspect);

  const positionById = new Map<string, NormalizedPoint>();
  for (const s of states) positionById.set(s.id, { x: s.x, y: s.y });

  const nodes: LayoutNode[] = states.map((s) => ({
    nodeId: s.id,
    position: { x: s.x, y: s.y },
    radius: s.radius,
    sizeTier: s.sizeTier,
  }));

  const minimap: MinimapDot[] = states.map((s) => ({
    nodeId: s.id,
    position: { x: s.x, y: s.y },
    sizeTier: s.sizeTier,
  }));

  const edges = buildEdges(input, positionById);

  return { nodes, edges, minimap, seed: input.seed };
}
